import {
    FetchState,
    AuthorizedSigner,
    AuthorizedSignerState,
    AddAuthorizedSignerRequest
} from "@/shared/types";
import * as bankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import * as _ from "lodash";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import * as Ssn from "@/shared/ssn";

export const initialState: AuthorizedSignerState = {
    fetchState: FetchState.Uninitialized,
    authorizedSigners: {},
    canOrderDebitCard: false,
    debitCardEligibilityFetchState: FetchState.Uninitialized
};

export const useAuthorizedSignerStore = defineStore("authorizedSigner", {
    state: (): AuthorizedSignerState => (_.cloneDeep(initialState)),
    getters: {
        isAuthoriziedSignersLoaded: state => state.fetchState === FetchState.Loaded,
        isDebitCardEligibilityLoading: state => state.debitCardEligibilityFetchState === FetchState.Loading,
        isDebitCardEligibilityLoaded: state => state.debitCardEligibilityFetchState === FetchState.Loaded,
        isDebitCardEligibilityLoadFailed: state => state.debitCardEligibilityFetchState === FetchState.Failed,
        isEligibleForDebitCard: state => state.debitCardEligibilityFetchState === FetchState.Loaded && state.canOrderDebitCard
    },
    actions: {
        loadAuthorizedSigners (args?: { force?: boolean }) {
            if (this.fetchState !== FetchState.Uninitialized && !(args && args.force)) {
                return Promise.resolve();
            }

            this.fetchState = FetchState.Loading;

            return bankService.getAuthorizedSigners()
                .then(response => {
                    this.authorizedSigners = response.data.authorizedSigners.reduce((accumulator, current) => {
                        accumulator[current.id] = current;
                        return accumulator;
                    }, {});
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                })
                .finally(() => {
                    this.fetchState = FetchState.Loaded;
                });
        },
        addAuthorizedSigner (request: AddAuthorizedSignerRequest) {
            return bankService.addAuthorizedSigner(request)
                .then(response => {
                    const newAuthorizedSigner = response.data.authorizedSigner;
                    const authorizedSigners = _.cloneDeep(this.authorizedSigners);

                    authorizedSigners[newAuthorizedSigner.id] = newAuthorizedSigner;
                    this.authorizedSigners = authorizedSigners;

                    const notifierStore = useNotifierStore();
                    if (request.orderDebitCard) {
                        notifierStore.setSuccess("Authorized signer updated. Debit card arrives in about 1 week.");
                    }
                    else {
                        notifierStore.setSuccess("Authorized signer updated.");
                    }
                })
                .catch(error => {
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },
        updateAuthorizedSigner (params: { id: number, authorizedSigner: AuthorizedSigner }) {
            return bankService.updateAuthorizedSigner(params.id, params.authorizedSigner)
                .then(response => {
                    const updatedAuthorizedSigner = response.data.authorizedSigner;
                    this.authorizedSigners[updatedAuthorizedSigner.id] = updatedAuthorizedSigner;

                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Authorized signer updated.");
                })
                .catch(error => {
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },
        loadDebitCardEligibility () {
            if (this.debitCardEligibilityFetchState === FetchState.Loaded) {
                return Promise.resolve();
            }

            this.debitCardEligibilityFetchState = FetchState.Loading;

            return bankService.getDebitCardEligibilityForAuthorizedSigner ()
                .then(response => {
                    if (response && response.data) {
                        this.canOrderDebitCard = response.data.canOrder;
                        this.debitCardEligibilityFetchState = FetchState.Loaded;
                    } else {
                        this.debitCardEligibilityFetchState = FetchState.Failed;
                    }
                })
                .catch(_ => {
                    this.debitCardEligibilityFetchState = FetchState.Failed;
                });
        },
        isAuthorizedSigner (ssn: string): boolean {
            if (Ssn.isInvalid(ssn)) {
               return false;
            }
            const authorizedSigners = Object.keys(this.authorizedSigners).map(key => this.authorizedSigners[key]);
            const index =  authorizedSigners.findIndex(x => Ssn.withoutDashes(x.ssn) === Ssn.withoutDashes(ssn));
            return index !== null && index !== undefined && index >= 0;
        }
    }
});
